import { useSyncExternalStore } from 'react';
import { cancelFileBrowserUpload, newFileUploadId, uploadFileBrowserFile, type BrowserUploadSource } from '../../api/fileBrowserUpload';
import type { FolderLocation } from './explorerApi';
import { createTransferMeter, type TransferMetrics } from './transferMetrics';

export interface TransferJob { id: string; file: BrowserUploadSource; target: FolderLocation; received: number; state: 'queued' | 'running' | 'paused' | 'done' | 'cancelling' | 'cancelled' | 'error'; message: string; metrics?: TransferMetrics }
let jobs: TransferJob[] = [];
let active: { id: string; controller: AbortController; promise: Promise<void> } | null = null;
const listeners = new Set<() => void>();
const publish = () => { jobs = jobs.slice(); listeners.forEach(listener => listener()); };
const update = (id: string, values: Partial<TransferJob>) => { jobs = jobs.map(j => j.id === id ? { ...j, ...values } : j); publish(); };
const incomplete = (j: TransferJob) => !['done', 'cancelled'].includes(j.state);
const finishActive = (id: string) => { if (active?.id === id) active = null; };

function next() {
  if (active) return;
  const job = jobs.find(j => j.state === 'queued');
  if (!job) return;
  const controller = new AbortController();
  const promise = (async () => {
    // Defer one microtask so active is set before a synchronous validation
    // failure can clear it or the next queued item starts.
    await Promise.resolve();
    update(job.id, { state: 'running', message: 'Подключаемся…' });
    const meter = createTransferMeter(job.file.size, job.metrics);
    const timer = setInterval(() => {
      if (jobs.find(j => j.id === job.id)?.state === 'running') update(job.id, { metrics: meter.snapshot() });
    }, 1000);
    try {
      await uploadFileBrowserFile(job.file, job.target, job.id,
        received => update(job.id, { received, metrics: meter.record(received) }), message => update(job.id, { message }), controller.signal);
      update(job.id, { state: 'done', received: job.file.size, message: 'Сохранено', metrics: meter.stop() });
    } catch (error) {
      const current = jobs.find(j => j.id === job.id);
      if (current?.state !== 'cancelling') update(job.id, { state: controller.signal.aborted ? 'paused' : 'error', message: controller.signal.aborted ? 'Приостановлено' : error instanceof Error ? error.message : 'Передача прервалась', metrics: meter.stop() });
    } finally {
      clearInterval(timer);
      finishActive(job.id);
      next();
    }
  })();
  active = { id: job.id, controller, promise };
}

export const transfers = {
  reset() { active?.controller.abort(); active = null; jobs = []; publish(); },
  add(this: void, files: BrowserUploadSource[], target: FolderLocation) {
    jobs = [...jobs.filter(incomplete), ...files.map(file => ({ id: newFileUploadId(), file, target: structuredClone(target), received: 0, state: 'queued' as const, message: 'В очереди' }))];
    publish(); next();
  },
  pause(id: string) { if (active?.id === id) active.controller.abort(); else update(id, { state: 'paused', message: 'Приостановлено' }); },
  resume(id: string) { const job = jobs.find(j => j.id === id); if (job && ['paused', 'error'].includes(job.state)) { update(id, { state: 'queued', message: 'Продолжение…' }); next(); } },
  async cancel(id: string) {
    const job = jobs.find(j => j.id === id);
    if (!job || !incomplete(job)) return;
    update(id, { state: 'cancelling', message: 'Отмена…' });
    if (active?.id === id) { active.controller.abort(); await active.promise; }
    try {
      const result = await cancelFileBrowserUpload(job.file, job.target, job.id);
      update(id, result.status === 'complete' ? { state: 'done', message: 'Файл уже сохранён', received: job.file.size } : { state: 'cancelled', message: 'Отменено' });
    } catch (error) { update(id, { state: 'error', message: error instanceof Error ? error.message : 'Не удалось отменить передачу' }); }
  },
  clearDone() { jobs = jobs.filter(incomplete); publish(); },
};
export function useExplorerTransfers() {
  return useSyncExternalStore(listener => { listeners.add(listener); return () => { listeners.delete(listener); }; }, () => jobs);
}
if (typeof window !== 'undefined') window.addEventListener('beforeunload', event => {
  if (jobs.some(incomplete)) { event.preventDefault(); event.returnValue = ''; }
});
